<template>
  <v-dialog v-model="dialogModel" max-width="700px" persistent role="dialog" aria-labelledby="group-details-title">
    <v-card class="group-details-card">
      <!-- Header with close button -->
      <div class="dialog-header">
        <div class="px-4 pt-2 pb-2 overflow-hidden  ">
          <h2 class="group-title">{{ group.fullName || group.shortName }}</h2>
        </div>
        <div class="actions-right">
          <v-btn color="primary" flat @click="editGroup">
            <v-icon left>edit</v-icon>
            {{ $i18n.t("Edit Group") }}
          </v-btn>
          <v-btn icon flat @click="closeDialog" class="close-btn">
            <v-icon>close</v-icon>
          </v-btn>
        </div>
      </div>
      <div class="px-4">
        <div class="detail-row">
          <div class="detail-label">
            <v-icon small class="mr-2">access_time</v-icon>
            {{ $i18n.t("Created time") }}
          </div>
          <div class="detail-value">{{ formatDate(group.creation) }}</div>
        </div>

        <div class="detail-row">
          <div class="detail-label">
            <v-icon small class="mr-2">flag</v-icon>
            {{ $i18n.t("Type") }}
          </div>
          <div class="detail-value">
            <v-chip v-if="group.type" small :color="getTypeColor(group.type)" text-color="white" class="type-badge">
              {{ $i18n.t(capitalize(group.type)) }}
            </v-chip>
            <span v-else class="no-type">-</span>
          </div>
        </div>

        <div class="detail-row">
          <div class="detail-label">
            <v-icon small class="mr-2">account_tree</v-icon>
            {{ $i18n.t("Parent") }}
          </div>
          <div class="detail-value">
            <template v-if="parentGroup">
              {{ parentGroup.fullName || parentGroup.shortName }}
            </template>
            <span v-else class="grey--text">{{ $i18n.t("None") }}</span>
          </div>
        </div>

        <div class="detail-row">
          <div class="detail-label">
            <v-icon small class="mr-2">business</v-icon>
            {{ $i18n.t("Client") }}
          </div>
          <div class="detail-value">
            <template v-if="clientName">
              {{ clientName }}
            </template>
            <span v-else class="grey--text">{{ $i18n.t("None") }}</span>
          </div>
        </div>

        <div class="detail-row">
          <div class="detail-label">
            <v-icon small class="mr-2">description</v-icon>
            {{ $i18n.t("Contract") }}
          </div>
          <div class="detail-value">
            <template v-if="contractName">
              {{ contractName }}
            </template>
            <span v-else class="grey--text">{{ $i18n.t("None") }}</span>
          </div>
        </div>

        <div class="detail-row">
          <div class="detail-label">
            <v-icon small class="mr-2">people</v-icon>
            {{ $i18n.t("Manager") }}
          </div>
          <div class="detail-value">
            <div class="assignees-container">
              <template v-if="managerUser">
                <v-avatar size="24" class="mr-1">
                  <v-icon small>person</v-icon>
                </v-avatar>
                {{ managerUser.name }}
              </template>
              <span v-else class="grey--text">{{ $i18n.t("None") }}</span>
            </div>
          </div>
        </div>
      </div>
      <v-tabs v-model="activeTab" slider-color="primary" class="group-tabs">
        <v-tab>{{ $i18n.t("Users") }}</v-tab>
        <v-tab>{{ $i18n.t("Details") }}</v-tab>
      </v-tabs>

      <v-tabs-items v-model="activeTab">
        <v-tab-item>
          <div class="tab-content pa-4">
            <!-- Users list could go here -->
            <div v-if="group.users && group.users.length > 0">
              <div v-for="user in group.users" :key="user" class="user-list-item">
                <v-avatar size="32" class="mr-2">
                  <v-icon>person</v-icon>
                </v-avatar>
                <div class="user-details">
                  <div class="user-name">{{ getUserName(user.userInfo) }}</div>
                  <div class="user-role">{{ $i18n.t(user.role) }}</div>
                </div>
              </div>
            </div>
            <div v-else class="no-data-message">
              {{ $i18n.t("No users in this group") }}
            </div>
          </div>
        </v-tab-item>
        <v-tab-item>
          <div class="tab-content pa-4">
            <div class="detail-item">
              <div class="detail-item-label">{{ $i18n.t("Address") }}</div>
              <div class="detail-item-value">{{ group.address || $i18n.t("Not specified") }}</div>
            </div>
            <div class="detail-item">
              <div class="detail-item-label">{{ $i18n.t("Short Name") }}</div>
              <div class="detail-item-value">{{ group.shortName }}</div>
            </div>
            <div class="detail-item">
              <div class="detail-item-label">{{ $i18n.t("Group Description") }}</div>
              <div class="detail-item-value">
                {{ group.description || $i18n.t("No description provided for this group.") }}
              </div>
            </div>
          </div>
        </v-tab-item>
      </v-tabs-items>
    </v-card>
  </v-dialog>
</template>

<script>
import { mapGetters } from "vuex";
import { GROUP_TYPE_COLOR } from "../../constants";

export default {
  name: "GroupDetails",
  props: {
    dialog: {
      type: Boolean,
      required: true
    },
    group: {
      type: Object,
      required: true
    }
  },
  data() {
    return {
      activeTab: 0
    };
  },
  computed: {
    ...mapGetters({
      groups: "group/getGroups",
      users: "users/getUsers",
      clients: "client/getClients",
      contracts: "contract/getContractsMeta"
    }),
    dialogModel: {
      get() {
        return this.dialog;
      },
      set(value) {
        if (!value) {
          this.$emit("close");
        }
      }
    },
    parentGroup() {
      if (!this.group.parent) return null;
      return this.groups.find(g => g._id === this.group.parent);
    },
    managerUser() {
      if (!this.group.manager) return null;
      console.log(this.group.manager, this.users);
      return this.users.find(u => u._id === this.group.manager);
    },
    clientName() {
      if (!this.group.client) return null;
      const client = this.clients.find(c => c._id === this.group.client);
      return client ? client.name : this.group.client;
    },
    contractName() {
      if (!this.group.contract) return null;
      const contract = this.contracts.find(c => c._id === this.group.contract);
      return contract ? contract.name : this.group.contract;
    }
  },
  created() {
    // Ensure we have the latest data
    this.$store.dispatch("client/fetchClients");
    this.$store.dispatch("group/fetchGroups");
    this.$store.dispatch("users/fetchUsers");
    this.$store.dispatch("contract/fetchContractsMeta");
  },
  methods: {
    closeDialog() {
      this.$emit("close");
    },
    editGroup() {
      this.$emit("close");
      this.$emit("edit", this.group);
    },
    getUserName(userId) {
      const user = this.users.find(u => u.user === userId);
      return user ? user.name : userId;
    },
    getUserRole(userId) {
      const user = this.users.find(u => u.user === userId);
      return user ? user.role : userId;
    },
    getTypeColor(type) {
      return GROUP_TYPE_COLOR[type] || "grey";
    },
    formatDate(dateString) {
      if (!dateString) return "-";
      const date = new Date(dateString);
      return new Intl.DateTimeFormat(this.$i18n.locale, {
        year: "numeric",
        month: "long",
        day: "numeric",
        hour: "2-digit",
        minute: "2-digit"
      }).format(date);
    },
    formatTime(dateString) {
      if (!dateString) return "";
      const date = new Date(dateString);
      return new Intl.DateTimeFormat(this.$i18n.locale, {
        hour: "2-digit",
        minute: "2-digit"
      }).format(date);
    },
    capitalize(str) {
      return str.charAt(0).toUpperCase() + str.slice(1);
    }
  }
};
</script>

<style scoped>
.group-details-card {
  border-radius: 4px;
  overflow: hidden;
}

.dialog-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 8px;
  border-bottom: 1px solid #e0e0e0;
}

.actions-right {
  display: flex;
}

.group-title {
  font-size: 20px;
  font-weight: 500;
  color: #333;
  margin: 0;
}

.detail-row {
  display: flex;
  padding: 12px 0;
  border-bottom: 1px solid #f0f0f0;
}

.detail-label {
  width: 140px;
  color: #666;
  font-size: 14px;
  display: flex;
  align-items: center;
}

.detail-value {
  flex: 1;
  font-size: 14px;
  color: #333;
}

.status-indicator {
  display: inline-block;
  width: 8px;
  height: 8px;
  border-radius: 50%;
  margin-right: 6px;
}

.status-department {
  background-color: #ff9800;
}

.status-team {
  background-color: #4caf50;
}

.status-division {
  background-color: #2196f3;
}

.status-branch {
  background-color: #9c27b0;
}

.status-default {
  background-color: #9e9e9e;
}

.tag-chip {
  font-size: 12px;
  height: 24px !important;
  background-color: #f5f5f5 !important;
  color: #666 !important;
}

.assignees-container {
  display: flex;
  align-items: center;
}

.section-title {
  font-size: 15px;
  font-weight: 500;
  color: #333;
  margin-bottom: 12px;
}

.description-text {
  font-size: 14px;
  color: #555;
  line-height: 1.5;
  white-space: pre-line;
}

.users-list {
  margin-top: 8px;
}

.user-item {
  display: flex;
  align-items: center;
  margin-bottom: 8px;
  font-size: 14px;
}

.more-users {
  font-size: 13px;
  color: #666;
  margin-top: 8px;
}

.no-users {
  font-size: 14px;
  color: #666;
  font-style: italic;
  margin-top: 8px;
}

.group-tabs {
  margin-top: 16px;
  border-top: 1px solid #e0e0e0;
}

.tab-content {
  min-height: 150px;
}

.activity-header {
  font-size: 13px;
  font-weight: 500;
  color: #666;
  margin-bottom: 12px;
}

.activity-item {
  display: flex;
  align-items: center;
  margin-bottom: 12px;
  font-size: 14px;
}

.user-name {
  font-weight: 500;
  margin-right: 6px;
}

.activity-text {
  color: #555;
}

.activity-time {
  margin-left: auto;
  color: #999;
  font-size: 12px;
}

.detail-item {
  margin-bottom: 16px;
}

.detail-item-label {
  font-size: 13px;
  color: #666;
  margin-bottom: 4px;
}

.detail-item-value {
  font-size: 14px;
  color: #333;
}

.user-list-item {
  display: flex;
  align-items: center;
  padding: 12px 0;
  border-bottom: 1px solid #f0f0f0;
}

.user-details {
  display: flex;
  flex-direction: column;
}

.user-role {
  font-size: 12px;
  color: #666;
}

.no-data-message {
  color: #666;
  font-style: italic;
  padding: 16px 0;
}
</style>
